import { scaleLabel } from "../../data/scale/scale";

/** The prescription dose a product fills (backend `linkedDose`). */
export type LinkedDose = {
  prescriptionId: string;
  drugName: string;
  /** Strength unit, e.g. mg. */
  type: string;
  form: string;
  doseId: string;
  doseValue: string;
};

/**
 * Units where one unit is one dose, so the linked dose is the strength of a
 * single unit and qty is frequency × days. Mirrors FORMULA_SCALES in the
 * backend's utils/prescriptionQty.js.
 */
const PER_UNIT_SCALES = new Set(["tablet", "capsule", "u"]);

/** Units that are almost always a medicine, so an unlinked one is worth a warning. */
const MEDICINE_SCALES = new Set(["tablet", "capsule"]);

export const isPerUnitScale = (scale?: string) => PER_UNIT_SCALES.has(scale || "");

export const isMedicineScale = (scale?: string) => MEDICINE_SCALES.has(scale || "");

/** "Amoxicillin 250mg" */
export const doseText = (link: Pick<LinkedDose, "drugName" | "doseValue" | "type">) =>
  `${link.drugName} ${link.doseValue}${link.type || ""}`.trim();

/** Label above the dose buttons: "Strength of 1 tablet" or "Dose it fills". */
export const doseFieldLabel = (scale?: string) =>
  isPerUnitScale(scale)
    ? `Strength of 1 ${scaleLabel(scale).toLowerCase()}`
    : "Dose it fills";

/**
 * One line that says what the link means:
 * "1 Tablet = Amoxicillin 250mg", or for bottles and tubes, where one unit
 * holds many doses, "Fills Amoxicillin syrup 5ml".
 */
export const linkSummary = (link: LinkedDose, scale?: string) =>
  isPerUnitScale(scale)
    ? `1 ${scaleLabel(scale)} = ${doseText(link)}`
    : `Fills ${doseText(link)}`;
